// Sub-actions — finer-grained permissions INSIDE a zone the user already holds.
//
// The zone grid answers "can this person open Staff Notes, and can they write?"
// Sub-actions answer the next question down: they can write a note, but may they
// also copy it onto other children, or email leadership about it?
//
// DEFAULT ON. Anyone with the zone gets every sub-action unless an admin
// explicitly takes one away, so nobody loses access by upgrading and a new
// sub-action can be added here without silently switching it off for everyone.
// That is why storage records only the DENIED keys:
//     customPermissions.subActions = { notes: { addNotify: false } }
// An absent key means allowed.
//
// SCOPE OF ENFORCEMENT — read this before adding one. A sub-action is a
// CLIENT-side restraint within a zone the server has already granted. The
// Firestore rules gate at zone granularity, so unticking "copy to other
// students" hides the control; it does not make the write impossible for
// someone driving the SDK directly. Use them to shape what a role should
// routinely do, never as the boundary around protected data — that boundary is
// the zone itself, and the rules enforce it. (Adding rules clauses per
// sub-action is also not free: the engine has a hard expression budget that has
// already been hit once on this project.)
//
// Only list a sub-action here once its control is actually gated. An admin who
// unticks something that keeps working is worse off than one who was never
// offered the choice.

export const ZONE_SUBACTIONS = {
  notes: [
    { key: 'addCopyTo', label: 'Copy a note to other students' },
    { key: 'addNotify', label: 'Email a note to leadership / parents' },
    { key: 'raiseIncident', label: 'Raise an incident report from a note' },
  ],
  mtss: [
    { key: 'recalibrateLevel', label: 'Ask the AI to re-check the NCCD level' },
  ],
  goals: [
    { key: 'printPlan', label: 'Print / export the goal plan' },
  ],
  emails: [
    { key: 'sendEmail', label: 'Actually send email (otherwise record-only)' },
    { key: 'attachFiles', label: 'Attach files to an email' },
  ],
  confidential: [
    { key: 'emailHomeSituation', label: 'Email the Home Situation sheet (exec-level)' },
  ],
  interventions: [
    { key: 'archiveGroup', label: 'Archive / restore intervention groups' },
  ],
};

/** Every zone that has sub-actions, in the order they should render. */
export const ZONES_WITH_SUBACTIONS = Object.keys(ZONE_SUBACTIONS);

/** Sub-actions for a zone (empty array when it has none). */
export function subActionsFor(zone) {
  return ZONE_SUBACTIONS[zone] || [];
}

/**
 * Is a sub-action allowed, given a stored map of DENIALS?
 * Absent / malformed input means allowed — the default-on rule.
 */
export function subActionAllowed(subActions, zone, key) {
  if (!subActions || typeof subActions !== 'object') return true;
  const forZone = subActions[zone];
  if (!forZone || typeof forZone !== 'object') return true;
  return forZone[key] !== false;
}

/**
 * Build the stored map from the editor's tick state. Only DENIALS are kept, so
 * a user doc never carries a wall of `true`s and a newly-added sub-action is
 * on for everyone by default.
 */
export function collectSubActionDenials(isTicked) {
  const out = {};
  for (const [zone, actions] of Object.entries(ZONE_SUBACTIONS)) {
    for (const { key } of actions) {
      if (!isTicked(zone, key)) {
        out[zone] = out[zone] || {};
        out[zone][key] = false;
      }
    }
  }
  return out;
}
